import { useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  FormHelperText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/common/Button';
import type { PeriodType as _PeriodType } from '@/types/session';

const createSessionSchema = (t: (key: string) => string) =>
  z.object({
    facilityName: z.string().min(2, t('sessionSetup.validation.facilityRequired')),
    serviceName: z.string().min(2, t('sessionSetup.validation.serviceRequired')),
    serviceType: z.string().min(2, t('sessionSetup.validation.serviceTypeRequired')),
    wardName: z.string().min(2, t('sessionSetup.validation.wardRequired')),
    periodNumber: z.number().min(1, t('sessionSetup.validation.periodMin')),
    periodType: z.enum([
      'baseline',
      'post_intervention',
      'follow_up_1',
      'follow_up_2',
      'annual',
    ] as const),
    observerInitials: z
      .string()
      .min(2, t('sessionSetup.validation.observerRequired'))
      .max(4, t('sessionSetup.validation.observerMax')),
  });

export type SessionFormData = z.infer<ReturnType<typeof createSessionSchema>>;

interface SessionSetupProps {
  onSubmit: (data: SessionFormData) => void;
  loading?: boolean;
}

export function SessionSetup({ onSubmit, loading }: SessionSetupProps) {
  const { t } = useTranslation();
  const sessionSchema = useMemo(() => createSessionSchema(t), [t]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SessionFormData>({
    resolver: zodResolver(sessionSchema),
    defaultValues: {
      facilityName: '',
      serviceName: '',
      serviceType: '',
      wardName: '',
      periodNumber: 1,
      periodType: 'baseline',
      observerInitials: '',
    },
  });

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      <Controller
        name="facilityName"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('sessionSetup.facilityName')}
            placeholder={t('sessionSetup.facilityPlaceholder')}
            error={!!errors.facilityName}
            helperText={errors.facilityName?.message}
            fullWidth
            required
          />
        )}
      />

      <Controller
        name="serviceName"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('sessionSetup.serviceName')}
            placeholder={t('sessionSetup.servicePlaceholder')}
            error={!!errors.serviceName}
            helperText={errors.serviceName?.message}
            fullWidth
            required
          />
        )}
      />

      <Controller
        name="serviceType"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('sessionSetup.serviceType')}
            placeholder={t('sessionSetup.serviceTypePlaceholder')}
            error={!!errors.serviceType}
            helperText={errors.serviceType?.message}
            fullWidth
            required
          />
        )}
      />

      <Controller
        name="wardName"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('sessionSetup.wardName')}
            placeholder={t('sessionSetup.wardPlaceholder')}
            error={!!errors.wardName}
            helperText={errors.wardName?.message}
            fullWidth
            required
          />
        )}
      />

      <Controller
        name="periodNumber"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            type="number"
            label={t('sessionSetup.periodNumber')}
            error={!!errors.periodNumber}
            helperText={errors.periodNumber?.message}
            fullWidth
            required
            onChange={(e) => field.onChange(parseInt(e.target.value, 10))}
          />
        )}
      />

      <Controller
        name="periodType"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth error={!!errors.periodType}>
            <InputLabel>{t('sessionSetup.periodType')}</InputLabel>
            <Select {...field} label={t('sessionSetup.periodType')}>
              <MenuItem value="baseline">{t('periodTypes.baseline')}</MenuItem>
              <MenuItem value="post_intervention">{t('periodTypes.post_intervention')}</MenuItem>
              <MenuItem value="follow_up_1">{t('periodTypes.follow_up_1')}</MenuItem>
              <MenuItem value="follow_up_2">{t('periodTypes.follow_up_2')}</MenuItem>
              <MenuItem value="annual">{t('periodTypes.annual')}</MenuItem>
            </Select>
            {errors.periodType && (
              <FormHelperText>{errors.periodType.message}</FormHelperText>
            )}
          </FormControl>
        )}
      />

      <Controller
        name="observerInitials"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('sessionSetup.observerInitials')}
            placeholder={t('sessionSetup.observerPlaceholder')}
            error={!!errors.observerInitials}
            helperText={errors.observerInitials?.message}
            fullWidth
            required
            inputProps={{ maxLength: 4 }}
          />
        )}
      />

      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        loading={loading}
        sx={{ mt: 2 }}
      >
        {t('sessionSetup.startSession')}
      </Button>
    </Box>
  );
}
